import { z, type ZodType } from 'zod';
import {
  AgentInteractionRecordSchema,
  AgentQuestionSchema,
  BranchOptionSchema,
  CitationSourceSchema,
  DeployVersionEventSchema,
} from './sseShared.js';
import { CodevNativeReportSchema } from './codevNative.js';
import { PLAYBOOK_ENGINES } from './playbooks.js';

export const TurnPhaseEventSchema = z.object({
  type: z.literal('phase'), phase: z.string(), timestamp: z.number(),
});
export const TurnLogEventSchema = z.object({
  type: z.literal('log'), message: z.string(),
  level: z.enum(['info', 'warn', 'error']), timestamp: z.number(),
});
/** Partial token for real-time streaming. */
export const TurnTokenEventSchema = z.object({
  type: z.literal('token'), text: z.string(), timestamp: z.number(),
});
/** Extended-thinking delta — live status display only, never persisted. */
export const TurnThinkingEventSchema = z.object({
  type: z.literal('thinking'), text: z.string(), timestamp: z.number(),
});
/** Model started composing a tool call (input still streaming). */
export const TurnToolPendingEventSchema = z.object({
  type: z.literal('tool_pending'), tool_name: z.string(),
  activity_text: z.string(), timestamp: z.number(),
});
export const TurnErrorEventSchema = z.object({
  type: z.literal('error'), error: z.string(),
  code: z.string().optional(), timestamp: z.number(),
});
export const TurnDoneEventSchema = z.object({
  type: z.literal('done'), success: z.boolean(), timestamp: z.number(),
});
export const TurnKeepaliveEventSchema = z.object({
  type: z.literal('keepalive'), timestamp: z.number(),
});
export const TurnMetadataEventSchema = z.object({
  type: z.literal('metadata'), session_id: z.string(), timestamp: z.number(),
});
/** NOTE: distinct from the conversation stream's system_update (different payload). */
export const TurnSystemUpdateEventSchema = z.object({
  type: z.literal('system_update'), system_id: z.string().optional(), timestamp: z.number(),
});
/** System notification to display in UI. `message_id` (when set) is a
 *  deterministic id so a reconnect replay of the buffered event reconciles
 *  with the already-rendered note instead of duplicating it. */
export const TurnSystemMessageEventSchema = z.object({
  type: z.literal('system_message'), message: z.string(),
  message_id: z.string().optional(), timestamp: z.number(),
});
/** A spec/goal-judge poke, streamed so it renders live as a labeled judge
 *  bubble. `message_id` matches the persisted row so a later getMessages
 *  reload de-dupes it. */
export const TurnJudgeMessageEventSchema = z.object({
  type: z.literal('judge_message'), judge: z.enum(['spec', 'goal']),
  content: z.string(), message_id: z.string(), timestamp: z.number(),
});
export const TurnToolStartEventSchema = z.object({
  type: z.literal('tool_start'), tool_name: z.string(), tool_id: z.string(),
  tool_input: z.unknown(), activity_text: z.string(), timestamp: z.number(),
  // Names the subagent this call was made from, so the admin tool-call view can
  // attribute it. Absent on main-agent calls.
  agent_type: z.string().optional(),
});
export const TurnToolEndEventSchema = z.object({
  type: z.literal('tool_end'), tool_name: z.string(), tool_id: z.string(),
  output: z.unknown(), is_error: z.boolean(), summary: z.string(), timestamp: z.number(),
  // The reminders this result fed back into the model's context — the exact
  // block `sdk-integration.ts` injected as the hook's additionalContext, so the
  // admin tool-call view shows what the agent was actually handed.
  nudges: z.string().optional(),
});
export const TurnToolImagesEventSchema = z.object({
  type: z.literal('tool_images'), tool_name: z.string(),
  images: z.array(z.string()), image_labels: z.array(z.string()),
  timestamp: z.number(),
});
/** Durable image reference, shared by live chat and message history. */
export const ConversationImageSchema = z.object({
  id: z.uuid(), label: z.string(), offset: z.number().int().nonnegative(),
});
export type ConversationImage = z.infer<typeof ConversationImageSchema>;
export const TurnConversationImagesEventSchema = z.object({
  type: z.literal('conversation_images'),
  images: z.array(ConversationImageSchema), timestamp: z.number(),
});
/** A firm source the agent just used (a file/template/playbook it actively
 *  consulted). Emitted at the moment of use — at the tool_result boundary that
 *  cited it — so a turn emits this zero or more times. The array is the wire
 *  shape; the producer sends exactly one source per event, and repeats are not
 *  filtered: whichever assistant bubble collects them dedupes. jobs.ts forwards
 *  it live and persists it on the bubble that was open at that instant
 *  (metadata.sources), which is what anchors the chip to where the agent
 *  actually used the source rather than to whatever bubble ends the turn. */
export const TurnSourcesEventSchema = z.object({
  type: z.literal('sources'), sources: z.array(CitationSourceSchema), timestamp: z.number(),
});
/** Per-query token usage, streamed once per turn (a running snapshot) and
 *  again at completion (authoritative cost/output). `agent` buckets the main
 *  agent vs any sub-agent; `queryId` groups snapshots from the same query so
 *  consumers keep the latest per query. Streaming per turn means a run that
 *  times out before the result message still reported its peak context.
 *  peakContextTokens = max over turns of (input + cache_read +
 *  cache_creation) — the "bloat" metric. */
export const TurnUsageEventSchema = z.object({
  type: z.literal('usage'), agent: z.enum(['main', 'side']), queryId: z.string(),
  peakContextTokens: z.number(), totalOutputTokens: z.number(),
  numTurns: z.number(), totalCostUsd: z.number(), timestamp: z.number(),
});
export const TurnAskUserQuestionEventSchema = z.object({
  type: z.literal('ask_user_question'), requestId: z.string(), jobId: z.string(),
  questions: z.array(AgentQuestionSchema),
  questionType: z.enum(['question', 'confirmation']).optional(), timestamp: z.number(),
});
export const TurnQuestionExpiredEventSchema = z.object({
  type: z.literal('question_expired'), requestId: z.string(), timestamp: z.number(),
});
export const TurnBranchProposalEventSchema = z.object({
  type: z.literal('branch_proposal'), requestId: z.string(), jobId: z.string(),
  decisionName: z.string(), branches: z.array(BranchOptionSchema), timestamp: z.number(),
});
/** A resolved interactive exchange, streamed so the client can swap the live
 *  popup for an inline record. jobs.ts also persists it as a chat_messages
 *  row (metadata.interaction) so it reloads as a history card. */
export const TurnInteractionResolvedEventSchema = z.object({
  type: z.literal('interaction_resolved'), requestId: z.string(), jobId: z.string(),
  interaction: AgentInteractionRecordSchema, timestamp: z.number(),
});
/** Editable playbook-capture card (gap A): the agent drafts a playbook from
 *  the run and the engineer corrects name/description/body inline before it
 *  saves. */
export const TurnPlaybookDraftEventSchema = z.object({
  type: z.literal('playbook_draft'), requestId: z.string(), jobId: z.string(),
  draft: z.object({
    name: z.string(), description: z.string(), body: z.string(),
    kind: z.enum(['playbook', 'context', 'template']),
    engine: z.enum(PLAYBOOK_ENGINES).nullable(),
  }),
  timestamp: z.number(),
});
/** Terminal outcome of a draft-card save (discriminated on `outcome`): the
 *  tool re-sanitizes and writes AFTER the card submits, so the card must wait
 *  for this before claiming "saved". `name` (saved) is the server-sanitized
 *  name — drives the deep link; `error` is the agent-safe reason, shown
 *  verbatim in the card. Buffered with the turn stream, so a mid-turn
 *  reconnect replays it to the rebuilt card; once the turn ends it is gone —
 *  a later reload drops it harmlessly.
 *  Renders as anyOf in the OpenAPI artifact — the gate's deprecation walk does
 *  not descend anyOf, so field removals here are always BLOCKED (safe default). */
export const TurnPlaybookDraftResultEventSchema = z.union([
  z.object({
    type: z.literal('playbook_draft_result'), requestId: z.string(), jobId: z.string(),
    timestamp: z.number(), outcome: z.literal('saved'), name: z.string(),
  }),
  z.object({
    type: z.literal('playbook_draft_result'), requestId: z.string(), jobId: z.string(),
    timestamp: z.number(), outcome: z.literal('error'), error: z.string(),
  }),
]);
/** Zemax analyses the agent just ran, one entry per completed analysis, with
 *  the worker request params the tool resolved (0-based indices, snake_case
 *  worker body keys; system data and render flags excluded). Viewers already
 *  showing a matching analysis re-issue the same request, which lands on the
 *  orchestrator's result cache instead of recomputing; in the tabbed pane the
 *  first analysis's tab is selected. It never opens desktop windows — the
 *  agent's openAnalysisViewers tool (viewer_focus) does that.
 *  `tab_id` is null for analyses with no UI viewer. CODE V entries carry the
 *  completed report and selected design version so viewers do not rerun it. */
export const TurnZemaxAnalysisRunEventSchema = z.object({
  type: z.literal('zemax_analysis_run'),
  analyses: z.array(z.object({
    analysis_type: z.string(),
    tab_id: z.string().nullable(),
    endpoint: z.string(),
    params: z.record(z.string(), z.unknown()),
    codev_result: z.object({
      report: CodevNativeReportSchema,
      system_id: z.string(),
      system_version: z.number().int(),
    }).optional(),
  })),
  timestamp: z.number(),
});
/** Goal lifecycle (/design-goal command). Terminal statuses (achieved /
 *  infeasible / round_limit) mean the goal was cleared server-side.
 *  `message_id` is a deterministic id for the system note the client
 *  renders, so a reconnect replay reconciles instead of duplicating it. */
export const TurnGoalStatusEventSchema = z.object({
  type: z.literal('goal_status'),
  status: z.enum(['set', 'not_met', 'achieved', 'infeasible', 'round_limit']),
  goal: z.string(), reason: z.string().optional(), round: z.number().optional(),
  maxRounds: z.number().optional(), message_id: z.string().optional(),
  timestamp: z.number(),
});

/** The mirror-body gate (src/agent/mirrorBodyGate.ts) ended a turn without a
 *  passing check on a system the turn wrote: `failing` — a ray still crosses a
 *  mirror body after the gate's blocks; `not_rechecked` — `failing_version`
 *  failed and the final `version` could not be re-checked; `unchecked` — the
 *  check could not run at all (`unavailable`: service or model; `beam_splits`
 *  is no longer sent since every arm is traced (#7059), but notices persisted
 *  before that still carry it). Numbers only — the client words the notice.
 *  `surfaces` are the blocking mirrors on the primary arm (LLM JSON surface
 *  indices); `arm_surfaces` those in a beam-split arm, numbered as that arm's
 *  Lens Editor tab shows them (its projected index); absent when none.
 *  `unchecked_arms`: beam-split arms whose Optiland model could not be traced,
 *  so the findings do not cover them; absent when every audited arm was.
 *  `message_id` keys on the run, version and status, so a reconnect replay and
 *  the persisted row (metadata.mirrorBodyCheck) reconcile to one notice. */
const MIRROR_BODY_CHECK_ENVELOPE = {
  type: z.literal('mirror_body_check'),
  version: z.number().int(), message_id: z.string(), timestamp: z.number(),
};
const MIRROR_BODY_FINDINGS = {
  passback_rays: z.number().int(), unmodelled_shadow_rays: z.number().int(), surfaces: z.array(z.number().int()),
  arm_surfaces: z.array(z.object({ arm: z.number().int(), surface: z.number().int() })).optional(),
  unchecked_arms: z.array(z.number().int()).optional(),
};
export const TurnMirrorBodyCheckEventSchema = z.discriminatedUnion('status', [
  z.object({ ...MIRROR_BODY_CHECK_ENVELOPE, status: z.literal('failing'), ...MIRROR_BODY_FINDINGS }),
  z.object({ ...MIRROR_BODY_CHECK_ENVELOPE, status: z.literal('not_rechecked'), failing_version: z.number().int(), ...MIRROR_BODY_FINDINGS }),
  z.object({ ...MIRROR_BODY_CHECK_ENVELOPE, status: z.literal('unchecked'), reason: z.enum(['beam_splits', 'unavailable']) }),
]);

/** Wire event name → payload schema for GET /jobs/stream. Keys are the SSE
 *  `event:` names (= payload `type`, except deploy_version which has no type). */
export const TURN_STREAM_EVENTS: Record<string, ZodType> = {
  deploy_version: DeployVersionEventSchema,
  phase: TurnPhaseEventSchema,
  log: TurnLogEventSchema,
  token: TurnTokenEventSchema,
  thinking: TurnThinkingEventSchema,
  tool_pending: TurnToolPendingEventSchema,
  error: TurnErrorEventSchema,
  done: TurnDoneEventSchema,
  keepalive: TurnKeepaliveEventSchema,
  metadata: TurnMetadataEventSchema,
  system_update: TurnSystemUpdateEventSchema,
  system_message: TurnSystemMessageEventSchema,
  judge_message: TurnJudgeMessageEventSchema,
  tool_start: TurnToolStartEventSchema,
  tool_end: TurnToolEndEventSchema,
  tool_images: TurnToolImagesEventSchema,
  conversation_images: TurnConversationImagesEventSchema,
  sources: TurnSourcesEventSchema,
  usage: TurnUsageEventSchema,
  ask_user_question: TurnAskUserQuestionEventSchema,
  question_expired: TurnQuestionExpiredEventSchema,
  branch_proposal: TurnBranchProposalEventSchema,
  interaction_resolved: TurnInteractionResolvedEventSchema,
  playbook_draft: TurnPlaybookDraftEventSchema,
  playbook_draft_result: TurnPlaybookDraftResultEventSchema,
  goal_status: TurnGoalStatusEventSchema,
  zemax_analysis_run: TurnZemaxAnalysisRunEventSchema,
  mirror_body_check: TurnMirrorBodyCheckEventSchema,
};

/** Everything the turn stream emits EXCEPT deploy_version (which has no `type`
 *  discriminant and is typed separately). Backend `SSEEvent` derives from this. */
export const TurnEventSchema = z.union([
  TurnPhaseEventSchema, TurnLogEventSchema, TurnTokenEventSchema,
  TurnThinkingEventSchema, TurnToolPendingEventSchema, TurnErrorEventSchema,
  TurnDoneEventSchema, TurnKeepaliveEventSchema, TurnMetadataEventSchema,
  TurnSystemUpdateEventSchema, TurnSystemMessageEventSchema,
  TurnJudgeMessageEventSchema, TurnToolStartEventSchema, TurnToolEndEventSchema,
  TurnToolImagesEventSchema, TurnConversationImagesEventSchema, TurnSourcesEventSchema, TurnUsageEventSchema,
  TurnAskUserQuestionEventSchema, TurnQuestionExpiredEventSchema,
  TurnBranchProposalEventSchema, TurnInteractionResolvedEventSchema,
  TurnPlaybookDraftEventSchema, TurnPlaybookDraftResultEventSchema,
  TurnGoalStatusEventSchema, TurnZemaxAnalysisRunEventSchema,
  TurnMirrorBodyCheckEventSchema,
]);

export type TurnEvent = z.infer<typeof TurnEventSchema>;
